import { useCallback, useState } from 'react'
import useSWR, { mutate } from 'swr'
import type {
  RewardPackage,
  CreateRewardPackageDTO,
  UpdateRewardPackageDTO,
  AssignRewardPackageDTO,
} from 'types/rewards'
import { deleteWithToken, fetchWithToken, postWithToken, putWithToken } from './http'

interface UseFetchRewardPackagesReturn {
  packages: RewardPackage[] | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<RewardPackage[] | null>
}

export function useFetchRewardPackages(
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchRewardPackagesReturn {
  const reallyShouldFetch = organizationId?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch ? [`rewards/packages?organizationId=${organizationId}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<RewardPackage[] | null> => {
    const refetchedData = await mutate()
    return refetchedData as RewardPackage[] | null
  }

  return {
    packages: data?.packages as RewardPackage[] | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseFetchRewardPackageReturn {
  package: RewardPackage | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<RewardPackage | null>
}

export function useFetchRewardPackage(
  id: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchRewardPackageReturn {
  const reallyShouldFetch = id?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch ? [`rewards/packages/${id}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    }
  )

  const refetch = async (): Promise<RewardPackage | null> => {
    const refetchedData = await mutate()
    return refetchedData as RewardPackage | null
  }

  return {
    package: data as RewardPackage | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseCreateRewardPackageReturn {
  createPackage: (dto: CreateRewardPackageDTO) => Promise<RewardPackage>
  isLoading: boolean
  error: Error | null
}

export function useCreateRewardPackage(token: string): UseCreateRewardPackageReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createPackage = useCallback(
    (dto: CreateRewardPackageDTO): Promise<RewardPackage> => {
      setIsLoading(true)
      setError(null)

      return postWithToken('rewards/packages', token, dto)
        .then((response) => {
          return response as RewardPackage
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { createPackage, isLoading, error }
}

interface UseUpdateRewardPackageReturn {
  updatePackage: (id: string, dto: UpdateRewardPackageDTO) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useUpdateRewardPackage(token: string): UseUpdateRewardPackageReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updatePackage = useCallback(
    (id: string, dto: UpdateRewardPackageDTO): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return putWithToken(`rewards/packages/${id}`, token, dto)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { updatePackage, isLoading, error }
}

interface UseDeleteRewardPackageReturn {
  deletePackage: (id: string) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useDeleteRewardPackage(token: string): UseDeleteRewardPackageReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deletePackage = useCallback(
    (id: string): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return deleteWithToken(`rewards/packages/${id}`, token)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { deletePackage, isLoading, error }
}

interface UseDeleteRewardPackagesReturn {
  deletePackages: (ids: string[]) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useDeleteRewardPackages(token: string): UseDeleteRewardPackagesReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deletePackages = useCallback(
    async (ids: string[]): Promise<void> => {
      setIsLoading(true)
      setError(null)

      try {
        await Promise.all(ids.map((id) => deleteWithToken(`rewards/packages/${id}`, token)))
      } catch (err) {
        const error = err instanceof Error ? err : new Error('An error occurred')
        setError(error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return { deletePackages, isLoading, error }
}

interface UseCopyRewardPackageReturn {
  copyPackage: (id: string, newName: string) => Promise<RewardPackage>
  isLoading: boolean
  error: Error | null
}

export function useCopyRewardPackage(token: string): UseCopyRewardPackageReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const copyPackage = useCallback(
    async (id: string, newName: string): Promise<RewardPackage> => {
      setIsLoading(true)
      setError(null)

      try {
        // First fetch the existing package
        const existingPackage = (await fetchWithToken(`rewards/packages/${id}`, token, null)) as RewardPackage

        // Create a new package with the same rewards but new name
        const newPackage = await postWithToken('rewards/packages', token, {
          rootOrganizationId: existingPackage.rootOrganizationId,
          organizationId: existingPackage.organizationId,
          name: newName,
          rewards: existingPackage.rewards,
          isActive: existingPackage.isActive,
        })

        return newPackage as RewardPackage
      } catch (err) {
        const error = err instanceof Error ? err : new Error('An error occurred')
        setError(error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return { copyPackage, isLoading, error }
}

interface UseAssignRewardPackageReturn {
  assignPackage: (dto: AssignRewardPackageDTO) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useAssignRewardPackage(token: string): UseAssignRewardPackageReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const assignPackage = useCallback(
    (dto: AssignRewardPackageDTO): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return postWithToken(`rewards/packages/${dto.packageId}/assign`, token, dto)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { assignPackage, isLoading, error }
}

// Helper to invalidate packages cache
export function invalidateRewardPackagesCache(organizationId: string, token: string) {
  mutate([`rewards/packages?organizationId=${organizationId}`, token])
}
